/**
 * AI 组件核心 Hook（对齐 CompsAi.newDesign.md）
 * 处理：消息校验、event_hash/cache_hash、思考方法查询、缓存读写、模型调用、结果聚合。
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback } from 'react';
var API_PATHS = {
    getThinkingMethods: '/api/ai/get-thinking-methods',
    getCache: '/api/ai/get-cache',
    saveCache: '/api/ai/save-cache',
    callModel: '/api/ai/call-model',
};
function ensureSlash(path) {
    return path.startsWith('/') ? path : "/".concat(path);
}
/** 浏览器环境 SHA-256（与后端算法一致），兼容 crypto.subtle 不可用场景（如非 HTTPS、部分 iframe、Secure Context） */
function sha256Hex(input) {
    var _a;
    return __awaiter(this, void 0, Promise, function () {
        var enc, bytes, subtle, data, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    enc = new TextEncoder();
                    bytes = enc.encode(input);
                    subtle = (_a = globalThis.crypto) === null || _a === void 0 ? void 0 : _a.subtle;
                    if (!(subtle != null)) return [3 /*break*/, 4];
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, subtle.digest('SHA-256', bytes)];
                case 2:
                    data = _c.sent();
                    return [2 /*return*/, Array.from(new Uint8Array(data))
                            .map(function (b) { return b.toString(16).padStart(2, '0'); })
                            .join('')];
                case 3:
                    _b = _c.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/, sha256HexFallback(bytes)];
            }
        });
    });
}
/** 纯 JS SHA-256 回退实现（当 crypto.subtle 不可用时） */
function sha256HexFallback(bytes) {
    var K = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
    ];
    var H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    var len = bytes.length;
    var bits = len * 8;
    var padding = ((56 - (len % 64) + 64) % 64) || 64;
    var total = len + padding;
    var buf = new ArrayBuffer(total + 8);
    var view = new DataView(buf);
    var arr = new Uint8Array(buf);
    arr.set(bytes, 0);
    arr[len] = 0x80;
    view.setUint32(total, (bits / 0x100000000) >>> 0, false);
    view.setUint32(total + 4, bits >>> 0, false);
    var W = new Uint32Array(64);
    for (var off = 0; off < total; off += 64) {
        for (var i = 0; i < 16; i++) {
            W[i] = view.getUint32(off + i * 4, false);
        }
        for (var i = 16; i < 64; i++) {
            var s0 = ((W[i - 15] >>> 7) | (W[i - 15] << 25)) ^ ((W[i - 15] >>> 18) | (W[i - 15] << 14)) ^ (W[i - 15] >>> 3);
            var s1 = ((W[i - 2] >>> 17) | (W[i - 2] << 15)) ^ ((W[i - 2] >>> 19) | (W[i - 2] << 13)) ^ (W[i - 2] >>> 10);
            W[i] = (W[i - 16] + s0 + W[i - 7] + s1) >>> 0;
        }
        var a = H[0], b = H[1], c = H[2], d = H[3], e = H[4], f = H[5], g = H[6], h = H[7];
        for (var i = 0; i < 64; i++) {
            var S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
            var ch = (e & f) ^ (~e & g);
            var t1 = (h + S1 + ch + K[i] + W[i]) >>> 0;
            var S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
            var maj = (a & b) ^ (a & c) ^ (b & c);
            var t2 = (S0 + maj) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        H[0] = (H[0] + a) >>> 0;
        H[1] = (H[1] + b) >>> 0;
        H[2] = (H[2] + c) >>> 0;
        H[3] = (H[3] + d) >>> 0;
        H[4] = (H[4] + e) >>> 0;
        H[5] = (H[5] + f) >>> 0;
        H[6] = (H[6] + g) >>> 0;
        H[7] = (H[7] + h) >>> 0;
    }
    return H.map(function (v) { return v.toString(16).padStart(8, '0'); }).join('');
}
/**
 * 压缩：将 JSON 对象转为 gzip 的 ArrayBuffer。
 * 若环境中无 pako，则回退为 JSON 字符串的 UTF-8 字节（未压缩）。
 */
function compressToBuffer(obj) {
    return __awaiter(this, void 0, Promise, function () {
        var jsonStr, pako, compressed, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    jsonStr = JSON.stringify(obj);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, import('pako')];
                case 2:
                    pako = _b.sent();
                    compressed = pako.gzip(jsonStr);
                    return [2 /*return*/, compressed.buffer.slice(compressed.byteOffset, compressed.byteOffset + compressed.byteLength)];
                case 3:
                    _a = _b.sent();
                    return [2 /*return*/, new TextEncoder().encode(jsonStr).buffer];
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * 解压：从 ArrayBuffer/Blob 恢复为对象。
 * 若前端收到的是 gzip，需与后端约定一致；此处按「先尝试 gzip，失败则按 UTF-8 文本」处理。
 */
function decompressFromBlob(blob) {
    return __awaiter(this, void 0, Promise, function () {
        var buffer, bytes, pako, str, _a, str;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, blob.arrayBuffer()];
                case 1:
                    buffer = _b.sent();
                    bytes = new Uint8Array(buffer);
                    _b.label = 2;
                case 2:
                    _b.trys.push([2, 4, , 5]);
                    return [4 /*yield*/, import('pako')];
                case 3:
                    pako = _b.sent();
                    str = pako.ungzip(bytes, { to: 'string' });
                    return [2 /*return*/, JSON.parse(str)];
                case 4:
                    _a = _b.sent();
                    str = new TextDecoder().decode(bytes);
                    return [2 /*return*/, JSON.parse(str)];
                case 5: return [2 /*return*/];
            }
        });
    });
}
/**
 * 从 payload 中抽取「结构」：保留字段名与层级，将具体值替换为类型占位（null/[]/""），
 * 用于生成只与结构相关的 eventHash（与 CompsAi 中的实现保持一致）。
 */
function getPayloadStructure(val) {
    if (val === null || val === undefined)
        return null;
    if (Array.isArray(val)) {
        if (val.length === 0)
            return [];
        return [getPayloadStructure(val[0])];
    }
    if (typeof val === 'object') {
        var obj = val;
        var out = {};
        for (var _i = 0, _a = Object.keys(obj); _i < _a.length; _i++) {
            var k = _a[_i];
            out[k] = getPayloadStructure(obj[k]);
        }
        return out;
    }
    if (typeof val === 'string')
        return '';
    if (typeof val === 'number')
        return null;
    if (typeof val === 'boolean')
        return null;
    return null;
}
export function useAiComponentHook(options) {
    var _this = this;
    if (options === void 0) { options = {}; }
    var _a = options.apiBaseUrl, apiBaseUrl = _a === void 0 ? '' : _a, _b = options.fetchFn, fetchFn = _b === void 0 ? fetch : _b;
    var base = function (path) {
        var p = ensureSlash(path);
        if (!apiBaseUrl)
            return p;
        var root = apiBaseUrl.replace(/\/$/, '');
        return "".concat(root).concat(p);
    };
    var generateMsgId = useCallback(function () {
        return typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : "msg-".concat(Date.now(), "-").concat(Math.random().toString(36).slice(2));
    }, []);
    var formatTriggerTime = useCallback(function () {
        return new Date().toISOString();
    }, []);
    var generateEventHash = useCallback(function (eventContent) { return __awaiter(_this, void 0, Promise, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, sha256Hex(eventContent)];
        });
    }); }, []);
    var generateCacheHash = useCallback(function (eventHash, methodId, dataKeySuffix) { return __awaiter(_this, void 0, Promise, function () {
        var baseStr, suffix;
        return __generator(this, function (_a) {
            baseStr = "".concat(eventHash, "*").concat(methodId);
            suffix = dataKeySuffix != null && String(dataKeySuffix).trim() ? String(dataKeySuffix).trim() : '';
            return [2 /*return*/, sha256Hex(suffix ? "".concat(baseStr, "*").concat(suffix) : baseStr)];
        });
    }); }, []);
    var compressResult = useCallback(function (result) {
        return compressToBuffer(result);
    }, []);
    var decompressResult = useCallback(function (blob) { return __awaiter(_this, void 0, Promise, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, decompressFromBlob(blob)];
        });
    }); }, []);
    /**
     * 处理传入消息：按流程图「先查缓存、未命中再调 AI、结果写回缓存」。
     * @param msg 标准消息
     * @param clickedMethodId 若传入（如点击某个思考方式），则只处理该 method：先整理哈希查缓存，无缓存再调 AI 并保存
     * @param forceRefresh 为 true 时跳过缓存，直接从后端获取并更新缓存表（用于「刷新」按钮）
     * @param eventHashOverride 若传入（如后端返回的 event_hash），则用其请求思考方法，避免与库中事件不一致导致「未找到思考方法」
     */
    var handleIncomingMessage = useCallback(function (msg, clickedMethodId, forceRefresh, eventHashOverride) { return __awaiter(_this, void 0, Promise, function () {
        var requiredFields, missing, eventHash, structuredPayload, eventContentStr, apiUrl, thinkingMethodsRes, errorText, thinkingMethodsJson, thinkingMethods, rawPayload, storeIdVal, storeIdStr, dataFingerprint, dataPart, str, dataKeySuffix, results, _loop_1, i, byMethodId;
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        return __generator(this, function (_r) {
            switch (_r.label) {
                case 0:
                    requiredFields = [
                        'msgId',
                        'source',
                        'eventData',
                    ];
                    missing = requiredFields.filter(function (f) { return msg[f] == null; });
                    if (missing.length > 0) {
                        throw new Error("\u6D88\u606F\u683C\u5F0F\u9519\u8BEF\uFF0C\u7F3A\u5931\u5B57\u6BB5\uFF1A".concat(missing.join(', ')));
                    }
                    if (!(eventHashOverride && typeof eventHashOverride === 'string')) return [3 /*break*/, 1];
                    eventHash = eventHashOverride;
                    return [3 /*break*/, 3];
                case 1:
                    structuredPayload = getPayloadStructure(msg.eventData.payload);
                    eventContentStr = JSON.stringify(structuredPayload);
                    return [4 /*yield*/, generateEventHash(eventContentStr)];
                case 2:
                    eventHash = _r.sent();
                    _r.label = 3;
                case 3:
                    apiUrl = "".concat(base(API_PATHS.getThinkingMethods), "?eventHash=").concat(encodeURIComponent(eventHash), "&pageId=").concat(encodeURIComponent(msg.source.pageId));
                    console.log('📡 [useAiComponentHook] 请求思考方法:', {
                        url: apiUrl,
                        eventHash: eventHash,
                        pageId: msg.source.pageId,
                        componentId: msg.source.componentId,
                        clickedMethodId: clickedMethodId !== null && clickedMethodId !== void 0 ? clickedMethodId : '全部',
                    });
                    return [4 /*yield*/, fetchFn(apiUrl, { headers: { Authorization: ((_a = msg.context) === null || _a === void 0 ? void 0 : _a.token) || '' } })];
                case 4:
                    thinkingMethodsRes = _r.sent();
                    if (!!thinkingMethodsRes.ok) return [3 /*break*/, 6];
                    return [4 /*yield*/, thinkingMethodsRes.text().catch(function () { return thinkingMethodsRes.statusText; })];
                case 5:
                    errorText = _r.sent();
                    console.error('❌ [useAiComponentHook] 获取思考方法失败:', {
                        status: thinkingMethodsRes.status,
                        statusText: thinkingMethodsRes.statusText,
                        url: apiUrl,
                        errorText: errorText,
                    });
                    throw new Error("\u83B7\u53D6\u601D\u8003\u65B9\u6CD5\u5931\u8D25: ".concat(thinkingMethodsRes.status, " ").concat(thinkingMethodsRes.statusText).concat(errorText ? " - ".concat(errorText) : ''));
                case 6: return [4 /*yield*/, thinkingMethodsRes.json()];
                case 7:
                    thinkingMethodsJson = _r.sent();
                    console.log('✅ [useAiComponentHook] 思考方法响应:', {
                        hasData: !!(thinkingMethodsJson === null || thinkingMethodsJson === void 0 ? void 0 : thinkingMethodsJson.data),
                        isArray: Array.isArray(thinkingMethodsJson === null || thinkingMethodsJson === void 0 ? void 0 : thinkingMethodsJson.data),
                        dataLength: Array.isArray(thinkingMethodsJson === null || thinkingMethodsJson === void 0 ? void 0 : thinkingMethodsJson.data) ? thinkingMethodsJson.data.length : 'N/A',
                    });
                    thinkingMethods = Array.isArray(thinkingMethodsJson === null || thinkingMethodsJson === void 0 ? void 0 : thinkingMethodsJson.data)
                        ? thinkingMethodsJson.data
                        : (thinkingMethodsJson === null || thinkingMethodsJson === void 0 ? void 0 : thinkingMethodsJson.data)
                            ? [thinkingMethodsJson.data]
                            : [];
                    if (clickedMethodId) {
                        thinkingMethods = thinkingMethods.filter(function (m) { return m.method_id === clickedMethodId ||
                            m.id === clickedMethodId; });
                        if (thinkingMethods.length === 0) {
                            console.warn('⚠️ [useAiComponentHook] 未找到点击的思考方法:', {
                                clickedMethodId: clickedMethodId,
                                eventHash: eventHash,
                            });
                            throw new Error("\u672A\u627E\u5230\u601D\u8003\u65B9\u6CD5: ".concat(clickedMethodId));
                        }
                    }
                    if (thinkingMethods.length === 0) {
                        console.warn('⚠️ [useAiComponentHook] 未配置对应思考方法:', {
                            pageId: msg.source.pageId,
                            componentId: msg.source.componentId,
                            eventHash: eventHash,
                        });
                        throw new Error('未配置对应思考方法');
                    }
                    rawPayload = msg.eventData.payload;
                    storeIdVal = rawPayload &&
                        typeof rawPayload === 'object' &&
                        rawPayload.storeId;
                    storeIdStr = typeof storeIdVal === 'string' || typeof storeIdVal === 'number'
                        ? String(storeIdVal)
                        : '';
                    dataFingerprint = '';
                    if (!(rawPayload && typeof rawPayload === 'object')) return [3 /*break*/, 9];
                    dataPart = rawPayload.data;
                    str = JSON.stringify(dataPart != null ? dataPart : rawPayload);
                    return [4 /*yield*/, sha256Hex(str)];
                case 8:
                    dataFingerprint = (_r.sent()).slice(0, 24);
                    _r.label = 9;
                case 9:
                    dataKeySuffix = [storeIdStr, dataFingerprint].filter(Boolean).join('_') || undefined;
                    results = [];
                    _loop_1 = function (i) {
                        var method, cacheHash, useCached, cacheRes, cacheBody, cachePayload, compressedResult_1, blob, out, questionRaw, affWords, negWords, thinkingBlock, question, requiredActions, returnStructureRaw, retAff, retNeg, returnConstraintsBlock, returnStructure, baseData, timeFields, dataForModel, dataKeys, currentLen, modelRes, errText, modelResult, compressed, saveBody;
                        return __generator(this, function (_s) {
                            switch (_s.label) {
                                case 0:
                                    method = thinkingMethods[i];
                                    return [4 /*yield*/, generateCacheHash(eventHash, method.method_id, dataKeySuffix)];
                                case 1:
                                    cacheHash = _s.sent();
                                    useCached = false;
                                    if (!!forceRefresh) return [3 /*break*/, 5];
                                    return [4 /*yield*/, fetchFn("".concat(base(API_PATHS.getCache), "?cacheHash=").concat(encodeURIComponent(cacheHash)))];
                                case 2:
                                    cacheRes = _s.sent();
                                    if (!cacheRes.ok) return [3 /*break*/, 5];
                                    return [4 /*yield*/, cacheRes.json()];
                                case 3:
                                    cacheBody = _s.sent();
                                    cachePayload = (_b = cacheBody === null || cacheBody === void 0 ? void 0 : cacheBody.data) !== null && _b !== void 0 ? _b : cacheBody;
                                    compressedResult_1 = cachePayload === null || cachePayload === void 0 ? void 0 : cachePayload.compressedResult;
                                    blob = compressedResult_1 != null
                                        ? typeof compressedResult_1 === 'string'
                                            ? (function () {
                                                var bin = atob(compressedResult_1);
                                                var arr = new Uint8Array(bin.length);
                                                for (var j = 0; j < bin.length; j++)
                                                    arr[j] = bin.charCodeAt(j);
                                                return new Blob([arr]);
                                            })()
                                            : compressedResult_1 instanceof Blob
                                                ? compressedResult_1
                                                : new Blob([new Uint8Array(compressedResult_1)])
                                        : null;
                                    if (!blob) return [3 /*break*/, 5];
                                    return [4 /*yield*/, decompressFromBlob(blob)];
                                case 4:
                                    out = _s.sent();
                                    results.push(out);
                                    console.log('✅ [useAiComponentHook] 缓存命中，直接显示:', {
                                        methodId: method.method_id,
                                        cacheHash: cacheHash,
                                    });
                                    useCached = true;
                                    _s.label = 5;
                                case 5:
                                    if (useCached)
                                        return [2 /*return*/, "continue"];
                                    questionRaw = (((_c = method.detail) === null || _c === void 0 ? void 0 : _c.question) && String(method.detail.question).trim()) ||
                                        method.display_name ||
                                        method.brief_desc ||
                                        '请根据数据回答问题';
                                    affWords = Array.isArray((_d = method.detail) === null || _d === void 0 ? void 0 : _d.affirmative_words)
                                        ? method.detail.affirmative_words
                                        : [];
                                    negWords = Array.isArray((_e = method.detail) === null || _e === void 0 ? void 0 : _e.negative_words)
                                        ? method.detail.negative_words
                                        : [];
                                    thinkingBlock = affWords.length > 0 || negWords.length > 0
                                        ? "\n\n\u3010\u601D\u7EF4\u8981\u6C42\u4E0E\u7981\u6B62\u3011\n\u8981\u6C42\uFF1A".concat(affWords.join('，'), "\n\u7981\u6B62\uFF1A").concat(negWords.join('，'))
                                        : '';
                                    question = questionRaw + thinkingBlock;
                                    requiredActions = Array.isArray((_f = method.detail) === null || _f === void 0 ? void 0 : _f.required_actions)
                                        ? method.detail.required_actions
                                        : [];
                                    returnStructureRaw = ((_h = (_g = method.return_format) === null || _g === void 0 ? void 0 : _g.format_detail) === null || _h === void 0 ? void 0 : _h.structure) != null
                                        ? String(method.return_format.format_detail.structure)
                                        : '';
                                    retAff = Array.isArray((_k = (_j = method.return_format) === null || _j === void 0 ? void 0 : _j.format_detail) === null || _k === void 0 ? void 0 : _k.affirmative_words)
                                        ? method.return_format.format_detail.affirmative_words
                                        : [];
                                    retNeg = Array.isArray((_m = (_l = method.return_format) === null || _l === void 0 ? void 0 : _l.format_detail) === null || _m === void 0 ? void 0 : _m.negative_words)
                                        ? method.return_format.format_detail.negative_words
                                        : [];
                                    returnConstraintsBlock = retAff.length > 0 || retNeg.length > 0
                                        ? "\n\n\u8981\u6C42\uFF1A".concat(retAff.join('，'), "\n\u7981\u6B62\uFF1A").concat(retNeg.join('，'))
                                        : '';
                                    returnStructure = returnStructureRaw + returnConstraintsBlock;
                                    baseData = rawPayload &&
                                        typeof rawPayload === 'object' &&
                                        rawPayload.data != null &&
                                        typeof rawPayload.data === 'object' &&
                                        ('current' in rawPayload.data ||
                                            'last7Days' in rawPayload.data ||
                                            'last30Days' in rawPayload.data ||
                                            'last12Months' in rawPayload.data)
                                        ? rawPayload.data
                                        : rawPayload;
                                    timeFields = rawPayload && typeof rawPayload === 'object'
                                        ? {
                                            currentTime: rawPayload.currentTime,
                                            currentTimeLocal: rawPayload.currentTimeLocal,
                                            currentHour: rawPayload.currentHour,
                                        }
                                        : {};
                                    dataForModel = baseData && typeof baseData === 'object'
                                        ? __assign(__assign({}, baseData), timeFields) : baseData;
                                    if (process.env.NODE_ENV === 'development') {
                                        dataKeys = dataForModel && typeof dataForModel === 'object' ? Object.keys(dataForModel) : [];
                                        currentLen = dataForModel &&
                                            typeof dataForModel === 'object' &&
                                            dataForModel.current != null &&
                                            typeof dataForModel.current === 'object' &&
                                            Array.isArray((_o = dataForModel.current) === null || _o === void 0 ? void 0 : _o.dataSource)
                                            ? dataForModel.current.dataSource.length
                                            : 0;
                                        if (dataKeys.length === 0 || currentLen === 0) {
                                            console.warn('⚠️ [call-model] 发送的 data 为空或 current 无数据，模型可能无法给出与数据相关的价格:', {
                                                dataKeys: dataKeys,
                                                currentDataSourceLen: currentLen,
                                            });
                                        }
                                        console.log('📤 [call-model] 发送的请求体（可在控制台/Network 的 call-model 请求中查看）:', {
                                            question: question,
                                            returnStructure: returnStructure || '(无)',
                                            dataKeys: dataKeys,
                                            dataSummary: dataForModel && typeof dataForModel === 'object'
                                                ? Object.fromEntries(Object.entries(dataForModel).map(function (_a) {
                                                    var k = _a[0], v = _a[1];
                                                    return [
                                                        k,
                                                        v && typeof v === 'object' && 'dataSource' in v
                                                            ? "dataSource(".concat(Array.isArray(v.dataSource) ? v.dataSource.length : 0, "\u6761)")
                                                            : typeof v === 'object' ? Object.keys(v) : v,
                                                    ];
                                                }))
                                                : dataForModel,
                                            dataFull: dataForModel,
                                        });
                                    }
                                    return [4 /*yield*/, fetchFn(base(API_PATHS.callModel), {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                Authorization: ((_p = msg.context) === null || _p === void 0 ? void 0 : _p.token) || '',
                                            },
                                            body: JSON.stringify({
                                                question: question,
                                                requiredActions: requiredActions,
                                                returnStructure: returnStructure,
                                                data: dataForModel,
                                            }),
                                        })];
                                case 6:
                                    modelRes = _s.sent();
                                    if (!!modelRes.ok) return [3 /*break*/, 8];
                                    return [4 /*yield*/, modelRes.text()];
                                case 7:
                                    errText = _s.sent();
                                    throw new Error("\u6A21\u578B\u8C03\u7528\u5931\u8D25: ".concat(modelRes.status, " ").concat(errText));
                                case 8: return [4 /*yield*/, modelRes.json()];
                                case 9:
                                    modelResult = _s.sent();
                                    return [4 /*yield*/, compressToBuffer(modelResult)];
                                case 10:
                                    compressed = _s.sent();
                                    saveBody = {
                                        cacheHash: cacheHash,
                                        eventHash: eventHash,
                                        methodId: method.method_id,
                                        compressedResult: compressed,
                                        expireDays: 30,
                                    };
                                    return [4 /*yield*/, fetchFn(base(API_PATHS.saveCache), {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                Authorization: ((_q = msg.context) === null || _q === void 0 ? void 0 : _q.token) || '',
                                            },
                                            body: JSON.stringify(__assign(__assign({}, saveBody), { compressedResult: Array.from(new Uint8Array(compressed)) })),
                                        })];
                                case 11:
                                    _s.sent();
                                    results.push(modelResult);
                                    return [2 /*return*/];
                            }
                        });
                    };
                    i = 0;
                    _r.label = 10;
                case 10:
                    if (!(i < thinkingMethods.length)) return [3 /*break*/, 13];
                    return [5 /*yield**/, _loop_1(i)];
                case 11:
                    _r.sent();
                    _r.label = 12;
                case 12:
                    i++;
                    return [3 /*break*/, 10];
                case 13:
                    byMethodId = {};
                    thinkingMethods.forEach(function (m, idx) {
                        byMethodId[m.method_id] = results[idx];
                    });
                    return [2 /*return*/, byMethodId];
            }
        });
    }); }, [
        generateEventHash,
        generateCacheHash,
        decompressFromBlob,
        base,
        fetchFn,
    ]);
    return {
        generateMsgId: generateMsgId,
        formatTriggerTime: formatTriggerTime,
        generateEventHash: generateEventHash,
        generateCacheHash: generateCacheHash,
        compressResult: compressResult,
        decompressResult: decompressResult,
        handleIncomingMessage: handleIncomingMessage,
    };
}
